import { useCallback, useEffect, useState } from 'react';

const targetKey = target => JSON.stringify([
  target.actionId, target.workoutExerciseId, target.setId,
].map(String));

// Structural deliveries remain pending independently until their exact row is
// displayed. A later approval must not replace an earlier, slower delivery.
export function useCoachAddedSetEffects(scope) {
  const scopeKey = String(scope ?? '');
  const [stored, setStored] = useState({ scopeKey, targets: [] });
  useEffect(() => {
    setStored(current => current.scopeKey === scopeKey ? current : { scopeKey, targets: [] });
  }, [scopeKey]);
  const publish = useCallback(targets => {
    if (!Array.isArray(targets) || targets.length === 0) return;
    setStored(current => {
      const previous = current.scopeKey === scopeKey ? current.targets : [];
      const pending = new Map(previous.map(target => [targetKey(target), target]));
      targets.forEach(target => pending.set(targetKey(target), target));
      return { scopeKey, targets: [...pending.values()] };
    });
  }, [scopeKey]);
  const markDisplayed = useCallback(displayed => {
    setStored(current => {
      if (current.scopeKey !== scopeKey || displayed?.scopeKey !== scopeKey) return current;
      const key = targetKey(displayed);
      const targets = current.targets.filter(target => targetKey(target) !== key);
      return targets.length === current.targets.length ? current : { scopeKey, targets };
    });
  }, [scopeKey]);
  return {
    effects: stored.scopeKey === scopeKey ? stored : { scopeKey, targets: [] },
    publish,
    markDisplayed,
  };
}

// Structural confirmations deliberately remain separate from numeric-field
// validation. Exact canonical IDs are authored by the successful atomic write.
export function buildCoachAddedSetTargets(actions = [], response = {}) {
  const results = Array.isArray(response?.results) ? response.results : null;
  return (Array.isArray(actions) ? actions : []).flatMap(action => {
    const result = results ? results.find(item => item?.action_id === action?.action_id) : response;
    const applied = results
      ? result?.success === true && result?.terminal === true && result?.status === 'approved'
      : result?.success === true && result?.terminal !== false && (!result?.status || result.status === 'approved');
    const added = result?.added_set;
    if (action?.tool_name !== 'add_set' || !action.action_id || !applied || result?.already_applied === true
      || !added?.set_id || !added?.workout_exercise_id
      || String(added.workout_exercise_id) !== String(action.arguments?.workout_exercise_id)) return [];
    return [{ actionId: action.action_id, workoutExerciseId: added.workout_exercise_id, setId: added.set_id }];
  });
}
